"use client";

import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardTitle,
  CardDescription,
  CardDate,
} from "@/components/ui/card";
import { Clapperboard, PlayIcon } from "lucide-react";
import { cn, tinaFieldSafe } from "@/utils";

import { useQueryState } from "nuqs";
import Image from "next/image";
import ReactPlayerLazy from "@/components/ReactPlayerLazy";
import dayjs from "dayjs";
import { TaxonomyBadge } from "@/components/ui/badge";

export default function Video({
  video,
  variant = "card",
  size = "sm",
  playInDialog = true,
  animateOnHover = true,
  className = "",
  backgroundVariant = "default",
  children = null,
}: {
  video: {
    publishDate?: string;
    title: string;
    description?: string;
    url: string;
    thumbnail?: string;
    categories?: { category?: { id?: string; title?: string } | null }[];
    tags?: { tag?: { id?: string; title?: string } | null }[];
    _isTiptapVideo?: boolean;
  };
  variant: "card" | "card-horizontal" | "list-item";
  size?: "sm" | "md" | "lg";
  playInDialog?: boolean;
  animateOnHover?: boolean;
  className?: string;
  children?: React.ReactNode;
  backgroundVariant?: "default" | "light" | "transparent";
}) {
  const [, setVideoParam] = useQueryState("video");

  const [playInline, setPlayInline] = useState(false);

  const hasContent = video?.title || video?.description;

  const handleClick = () => {
    if (playInDialog) {
      setVideoParam(video.url);
    } else {
      setPlayInline(true);
    }
  };

  const categories = video?.categories?.filter(({ category }: any) => category);
  const tags = video?.tags?.filter(({ tag }: any) => tag);

  if (variant === "card" || variant === "card-horizontal") {
    return (
      <Card
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`Play video: ${video.title}`}
        wrapperClassName="!shadow-none"
        className={cn("cursor-pointer group h-full", className)}
        animateOnHover={animateOnHover}
        backgroundVariant={backgroundVariant}
      >
        <CardContent
          className={cn("grid flex-1 h-full ", {
            "grid-cols-1 grid-rows-[auto_minmax(0,1fr)]": variant === "card",
            "grid-cols-1 md:grid-cols-2 gap-12 items-center":
              variant === "card-horizontal",
            "gap-6": hasContent,
          })}
        >
          <VideoThumbnail
            video={video}
            playInDialog={playInDialog}
            playInline={playInline}
          />
          {hasContent && (
            <div
              className={cn("gap-2 flex-1 h-full flex flex-col", {
                "": variant === "card",
                "space-y-5": variant === "card-horizontal",
              })}
            >
              {video.publishDate && (
                <CardDate
                  className={cn("", {})}
                  data-tina-field={
                    !video._isTiptapVideo
                      ? tinaFieldSafe(video, "publishDate")
                      : undefined
                  }
                >
                  {dayjs(video?.publishDate).format("MMM D, YYYY")}
                </CardDate>
              )}
              {video.title && (
                <CardTitle
                  className={cn("!line-clamp-2", {
                    "line-clamp-3 h3": variant === "card-horizontal",
                    "flex-1": !video?.description,
                  })}
                  size={size}
                  title={video?.title}
                  data-tina-field={
                    !video._isTiptapVideo
                      ? tinaFieldSafe(video, "title")
                      : undefined
                  }
                >
                  {video?.title}
                </CardTitle>
              )}
              {video.description && (
                <CardDescription
                  size={size}
                  data-tina-field={
                    !video._isTiptapVideo
                      ? tinaFieldSafe(video, "description")
                      : undefined
                  }
                >
                  {video?.description}
                </CardDescription>
              )}
              {children && children}
              {(!!categories?.length || !!tags?.length) && (
                <div className="flex items-center flex-wrap gap-2 mt-6 min-w-0 overflow-hidden">
                  {categories?.map((item: any, index: number) => (
                    <TaxonomyBadge
                      key={item?.category?.id || `category-${index}`}
                      variant="outline"
                      title={item?.category?.title}
                    >
                      {item?.category?.title}
                    </TaxonomyBadge>
                  ))}
                  {tags?.map((item: any, index: number) => (
                    <TaxonomyBadge
                      key={item?.tag?.id || `tag-${index}`}
                      variant="outline"
                      title={item?.tag?.title}
                    >
                      {item?.tag?.title}
                    </TaxonomyBadge>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  } else if (variant === "list-item") {
    return (
      <div
        className="py-6 first:border-t border-b grid grid-cols-[1fr_4fr] gap-8 cursor-pointer group"
        role="button"
        tabIndex={0}
        aria-label={`Play video: ${video.title}`}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        <VideoThumbnail
          video={video}
          playInDialog={playInDialog}
          playInline={playInline}
        />
        {hasContent && (
          <div className="space-y-2">
            {video.publishDate && (
              <CardDate className={cn("mb-2", {})}>
                {dayjs(video.publishDate).format("MMM D, YYYY")}
              </CardDate>
            )}
            {video.title && <CardTitle size={size}>{video.title}</CardTitle>}
            {video.description && (
              <CardDescription size={size} className="!line-clamp-2">
                {video.description}
              </CardDescription>
            )}
            {/* {video.categories && ( */}
            <div className="flex items-center flex-wrap gap-2 mt-5 min-h-7 min-w-0 overflow-hidden">
              {categories?.map(
                (item: any, index: number) =>
                  item?.category && (
                    <TaxonomyBadge
                      key={item?.category?.id || `category-${index}`}
                      variant="outline"
                      title={item?.category?.title}
                    >
                      {item?.category?.title}
                    </TaxonomyBadge>
                  )
              )}
              {tags?.map(
                (item: any, index: number) =>
                  item?.tag && (
                    <TaxonomyBadge
                      key={item?.tag?.id || `tag-${index}`}
                      variant="outline"
                      title={item?.tag?.title}
                    >
                      {item?.tag?.title}
                    </TaxonomyBadge>
                  )
              )}
            </div>
            {/* )} */}
          </div>
        )}
      </div>
    );
  }
}

function youtubeToThumbnail(url) {
  // Return null early if url is not a string or is empty
  if (!url || typeof url !== "string" || !url.trim()) {
    return null;
  }

  try {
    const parsedUrl = new URL(url);
    const videoId = parsedUrl.searchParams.get("v");
    if (!videoId) return null; // No video ID found
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  } catch (e) {
    // Silently return null for invalid URLs instead of logging
    return null;
  }
}

function VideoThumbnail({
  video,
  playInDialog,
  playInline,
}: {
  video: {
    url: string;
    title: string;
    thumbnail?: string;
    _isTiptapVideo?: boolean;
  };
  playInDialog: boolean;
  playInline: boolean;
}) {
  const videoThumbnail = youtubeToThumbnail(video.url) || video.thumbnail;

  return (
    <div
      className="relative w-full aspect-video bg-gray-750 flex items-center justify-center"
      data-tina-field={
        !video._isTiptapVideo ? tinaFieldSafe(video, "url") : undefined
      }
    >
      <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
        <Clapperboard className="size-8 text-gray-800 stroke-[1.5px]" />
      </div>

      {videoThumbnail && (
        <>
          <Image
            src={videoThumbnail}
            alt={video.title}
            width={1226}
            height={690}
            quality={100}
            sizes="(max-width: 768px) 100vw, min(1226px, 90vw)"
            className="w-full h-full object-cover z-10"
          />
          <span
            className="absolute top-0 left-0 flex items-center justify-center z-20 w-full h-full pointer-events-none"
            aria-hidden
          >
            <PlayIcon className="size-14 stroke-[1.5px] bg-white/30 text-white stroke-white p-4 rounded-full group-hover:scale-110 transition-all duration-300" />
          </span>
        </>
      )}

      <>
        {playInline && !playInDialog && (
          <ReactPlayerLazy
            src={video.url}
            width="100%"
            height="100%"
            className="w-full aspect-video absolute top-0 left-0 inset-0 z-30"
            controls
            playing={true}
          />
        )}
      </>
    </div>
  );
}
